import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from '../../firebase';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, MagnifyingGlassIcon, ArrowDownTrayIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import MoonLoader from 'react-spinners/MoonLoader';
import BreadCrumb from '../BreadCrumb';
import { useAuth } from '../Auth/Auth';
import { formatDate, convertTimeStampToDate } from '../Help/helper';
import { CSVLink } from 'react-csv';

const pages = [
  { name: 'Service Advisor', to: '/vehicles', component: Link, current: false },
  { name: 'Open Job Cards', to: '/vehicles/openjobs', component: Link, current: true },
];



const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const { role } = useAuth();
  const [sortConfig, setSortConfig] = useState({ key: 'jobCardNo', direction: 'ascending' });
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState(10);
  const [goto, setGoto] = useState('');
  const navigate = useNavigate();
  const [searchCategory, setSearchCategory] = useState('customerName');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setIsLoading(true);
    const jobsCollection = collection(db, 'jobs');
    const openJobsQuery = query(jobsCollection, where('status', '==', 'Open'));
    const jobsSnapshot = await getDocs(openJobsQuery);
    const jobsList = await Promise.all(
      jobsSnapshot.docs.map(async (docSnapshot) => {
        const jobData = docSnapshot.data();
        return {
          ...jobData,
          id: docSnapshot.id,
        };
      })
    );
    setJobs(jobsList);
    setIsLoading(false);
  };

  const handleSearch = async () => {
    if (!searchQuery) {
      fetchJobs();
      return;
    }

    setIsLoading(true);

    const jobsCollection = collection(db, 'jobs');
    let searchQueryRef;

    if (searchCategory === 'customerName') {
      searchQueryRef = query(jobsCollection, where('customerName', '>=', searchQuery), where('customerName', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'VIN') {
      searchQueryRef = query(jobsCollection, where('VIN', '>=', searchQuery), where('VIN', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'jobCardNo') {
      searchQueryRef = query(jobsCollection, where('jobCardNo', '>=', searchQuery), where('jobCardNo', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'registrationNumber') {
      searchQueryRef = query(jobsCollection, where('registrationNumber', '>=', searchQuery), where('registrationNumber', '<=', searchQuery + '\uf8ff'));
    }

    const jobsSnapshot = await getDocs(searchQueryRef);
    const jobsList = jobsSnapshot.docs.map((docSnapshot) => ({
      id: docSnapshot.id,
      ...docSnapshot.data(),
    }));

    setJobs(jobsList);
    setIsLoading(false);
  };

  const sortData = (key, direction) => {
    setSortConfig({
      key: key,
      direction: direction
    });
  };

  const sortedJobs = () => {
    if (!sortConfig) return jobs;
    return [...jobs].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const currentJobs = sortedJobs().slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage);
  const totalPages = Math.ceil(jobs.length / jobsPerPage);
  const changePage = (pageNumber) => setCurrentPage(pageNumber);

  const navigateToJobDetail = (jobCardNo) => {
    navigate(`/vehicles/jobupdate/${jobCardNo}`);
  };

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }


  const getPageNumbers = () => {
  const total = totalPages;
  const current = currentPage;
  const delta = 2; // how many numbers to show on each side of current
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('…');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};

const handleGoto = () => {
  const target = Number(goto);
  if (!Number.isNaN(target) && target >= 1 && target <= totalPages) {
    changePage(target);
    setGoto('');
  }
};

  // CSV export setup
  const csvHeaders = [
    { label: 'Job No', key: 'jobCardNo' },
    { label: 'Customer Name', key: 'customerName' },
    { label: 'Reg No', key: 'registrationNumber' },
    { label: 'VIN', key: 'VIN' },
    { label: 'Vehicle Type', key: 'vehicleMake' },
    { label: 'Date Created', key: 'createdAt' },
  ];

  const normalizeJob = (j) => ({
    jobCardNo: j?.jobCardNo ?? '',
    customerName: j?.customerName ?? '',
    registrationNumber: j?.registrationNumber ?? '',
    VIN: j?.VIN ?? '',
    vehicleMake: j?.vehicleMake ?? '',
    createdAt: j?.createdAt?.toDate
      ? convertTimeStampToDate(j.createdAt)
      : (j?.createdAt ? formatDate(j.createdAt) : ''),
  });

  const csvDataAll = jobs.map(normalizeJob);
  const csvDataPage = currentJobs.map(normalizeJob);


  return (
    <>
      <BreadCrumb pages={pages} />

      {/* Gradient Background */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-4">
        <div className="container mx-auto px-4">

          {/* Page Header */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-lg">
                <BriefcaseIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900">Open Job Cards</h1>
                <p className="text-sm text-gray-600 mt-0.5">Manage and track active service jobs</p>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl shadow-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-semibold uppercase tracking-wide">Total Open Jobs</p>
                  <p className="text-4xl font-black mt-2">{jobs.length}</p>
                </div>
                <BriefcaseIcon className="h-16 w-16 text-blue-200 opacity-50" />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-lg">
              <div className="text-center">
                <MoonLoader size={60} color="#3B82F6" />
                <p className="mt-4 text-gray-600 font-medium">Loading open jobs...</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">

              {/* Search and Export Controls */}
              <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row gap-4">

                  {/* Search Section */}
                  <div className="flex-1 flex flex-col sm:flex-row gap-3">
                    <select
                      value={searchCategory}
                      onChange={(e) => setSearchCategory(e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="customerName">Customer Name</option>
                      <option value="VIN">VIN</option>
                      <option value="jobCardNo">Job Card No</option>
                      <option value="registrationNumber">Registration No</option>
                    </select>

                    <div className="flex-1 relative">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={`Search by ${searchCategory}...`}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      />
                    </div>

                    <button
                      onClick={handleSearch}
                      className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                    >
                      <MagnifyingGlassIcon className="h-5 w-5" />
                      Search
                    </button>
                  </div>

                  {/* Export Buttons */}
                  <div className="flex gap-2">
                    <CSVLink
                      data={csvDataPage}
                      headers={csvHeaders}
                      filename={`open_jobs_page_${currentPage}.csv`}
                      className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2 text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Page CSV
                    </CSVLink>
                    <CSVLink
                      data={csvDataAll}
                      headers={csvHeaders}
                      filename={`open_jobs_all.csv`}
                      className="px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2 text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      All CSV
                    </CSVLink>
                  </div>
                </div>
              </div>

              {/* Table */}
              {currentJobs.length === 0 ? (
                <div className="p-12 text-center">
                  <BriefcaseIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Open Jobs Found</h3>
                  <p className="text-gray-600">There are currently no open job cards to display.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-blue-500 to-cyan-600">
                      <tr>
                        {[
                          { label: 'Job No', key: 'jobCardNo' },
                          { label: 'Customer Name', key: 'customerName' },
                          { label: 'Reg No', key: 'registrationNumber' },
                          { label: 'VIN', key: 'VIN' },
                          { label: 'Vehicle Type', key: 'vehicleMake' },
                          { label: 'Date Created', key: 'createdAt' },
                        ].map((header) => (
                          <th
                            key={header.key}
                            className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-blue-600 transition-colors duration-150"
                            onClick={() => {
                              const newDirection = sortConfig.key === header.key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
                              sortData(header.key, newDirection);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {header.label}
                              {sortConfig.key === header.key && (
                                sortConfig.direction === 'ascending'
                                  ? <ChevronUpIcon className="h-4 w-4" />
                                  : <ChevronDownIcon className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                        ))}
                        <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {currentJobs.map((job, index) => (
                        <tr
                          key={job.id}
                          className="hover:bg-blue-50 transition-colors duration-150 cursor-pointer"
                          onClick={() => navigateToJobDetail(job.jobCardNo)}
                        >
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs font-bold text-blue-600">{job.jobCardNo}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs font-medium text-gray-900">{job.customerName}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{job.registrationNumber}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{job?.VIN}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{`${job.vehicleMake} ${job.vehicleModel}`}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{formatDate(job.createdAt.toDate())}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToJobDetail(job.jobCardNo);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:shadow-md"
                            >
                              <PencilSquareIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {currentJobs.length > 0 && (
                <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                    {/* Rows per page */}
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700">Rows per page:</span>
                      <select
                        value={jobsPerPage}
                        onChange={(e) => {
                          setJobsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <span className="text-sm text-gray-600">
                        {(jobs.length === 0)
                          ? '0–0 of 0'
                          : `${(currentPage - 1) * jobsPerPage + 1}–${Math.min(currentPage * jobsPerPage, jobs.length)} of ${jobs.length}`}
                      </span>
                    </div>

                    {/* Pagination controls */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => changePage(1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200 ${
                          currentPage === 1
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'hover:bg-blue-50 text-gray-700 border-gray-300'
                        }`}
                      >
                        First
                      </button>
                      <button
                        onClick={() => changePage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200 ${
                          currentPage === 1
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'hover:bg-blue-50 text-gray-700 border-gray-300'
                        }`}
                      >
                        Prev
                      </button>

                      {getPageNumbers().map((p, idx) =>
                        p === '…' ? (
                          <span key={`dots-${idx}`} className="px-2 py-1.5 text-sm text-gray-500">…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => changePage(p)}
                            className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200 ${
                              p === currentPage
                                ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white border-transparent shadow-md'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'
                            }`}
                          >
                            {p}
                          </button>
                        )
                      )}

                      <button
                        onClick={() => changePage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200 ${
                          (currentPage === totalPages || totalPages === 0)
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'hover:bg-blue-50 text-gray-700 border-gray-300'
                        }`}
                      >
                        Next
                      </button>
                      <button
                        onClick={() => changePage(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200 ${
                          (currentPage === totalPages || totalPages === 0)
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'hover:bg-blue-50 text-gray-700 border-gray-300'
                        }`}
                      >
                        Last
                      </button>
                    </div>

                    {/* Go to page */}
                    <div className="flex items-center gap-2">
                      <label htmlFor="goto" className="text-sm font-medium text-gray-700">Go to:</label>
                      <input
                        id="goto"
                        type="number"
                        min={1}
                        max={totalPages || 1}
                        value={goto}
                        onChange={(e) => setGoto(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleGoto(); }}
                        className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="#"
                      />
                      <button
                        onClick={handleGoto}
                        disabled={!goto}
                        className="px-4 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg text-sm font-semibold hover:from-blue-600 hover:to-cyan-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Go
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Jobs;
